import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  IconButton,
  SimpleGrid,
  useColorModeValue,
  useToast,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
} from "@chakra-ui/react";
import { Search, Shield, LogOut, Trash2, ExternalLink, RefreshCw, UserPlus, X, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { getAdminScopeUserId } from "../../config/admin";
import { useAdminUnlock } from "./AdminGate";
import { GetAllUsers, DeleteUser } from "../../services/backend";
import {
  normalizeUsersList,
  sortUsersByNewest,
  userDisplayName,
  userSearchHaystack,
  formatUserGender,
  ADMIN_PROFILE_FILTERS,
  ADMIN_FILTER_GROUPS,
  userMatchesProfileFilters,
  countUsersForFilter,
} from "../../util/adminUsers";
import AdminUserEditor from "./AdminUserEditor";
import AdminDashboard from "./AdminDashboard";
import AdminReports from "./AdminReports";

const USERS_PAGE_SIZE = 100;

export default function AdminPage() {
  const toast = useToast();
  const { lockAdmin } = useAdminUnlock();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [page, setPage] = useState(0);
  const [editUserId, setEditUserId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteRef = React.useRef();
  const { isOpen: deleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const shellBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(18,18,20,0.88)");
  const shellShadow = useColorModeValue(
    "0 8px 40px rgba(15,23,42,0.08)",
    "0 12px 48px rgba(0,0,0,0.4)"
  );
  const border = useColorModeValue("gray.200", "whiteAlpha.100");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const titleColor = useColorModeValue("gray.900", "white");
  const rowHover = useColorModeValue("brand.50", "whiteAlpha.50");
  const inputBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const tabTrackBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const tableBg = useColorModeValue("white", "rgba(0,0,0,0.25)");
  const filterPanelBg = useColorModeValue("gray.50", "rgba(255,255,255,0.03)");
  const filterActiveBg = useColorModeValue("brand.50", "rgba(52,211,153,0.12)");
  const filterIdleHover = useColorModeValue("white", "whiteAlpha.100");
  const countIdleBg = useColorModeValue("gray.200", "whiteAlpha.200");
  const countIdleColor = useColorModeValue("gray.700", "whiteAlpha.800");
  const groupLabelColor = useColorModeValue("gray.500", "whiteAlpha.500");
  const optionLabelColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const tabSelected = {
    color: "gray.900",
    bgGradient: "linear(to-r, brand.300, brand.500)",
    boxShadow: "0 2px 10px rgba(52,211,153,0.25)",
  };
  const tabBase = {
    borderRadius: "full",
    fontSize: "sm",
    fontWeight: "semibold",
    px: 4,
    py: 2,
    color: muted,
    _selected: tabSelected,
    _hover: { bg: rowHover },
  };

  const loadUsers = useCallback(async () => {
    const scopeId = getAdminScopeUserId();
    if (!scopeId) {
      toast({ title: "Missing scope user ID", status: "warning" });
      return;
    }
    setLoading(true);
    try {
      const data = await GetAllUsers(scopeId);
      setUsers(sortUsersByNewest(normalizeUsersList(data)));
    } catch (err) {
      toast({
        title: "Failed to load users",
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 6000,
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (!userMatchesProfileFilters(u, activeFilters)) return false;
      if (!q) return true;
      return userSearchHaystack(u).includes(q);
    });
  }, [users, search, activeFilters]);

  // Reset to first page when search/filters change so counts stay coherent.
  useEffect(() => {
    setPage(0);
  }, [search, activeFilters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  const pageItems = useMemo(() => {
    const start = safePage * USERS_PAGE_SIZE;
    return filtered.slice(start, start + USERS_PAGE_SIZE);
  }, [filtered, safePage]);

  const rangeStart = filtered.length === 0 ? 0 : safePage * USERS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(filtered.length, (safePage + 1) * USERS_PAGE_SIZE);
  const canPrev = safePage > 0;
  const canNext = safePage < totalPages - 1 && filtered.length > 0;

  const filterCounts = useMemo(() => {
    const map = {};
    ADMIN_PROFILE_FILTERS.forEach((f) => {
      map[f.id] = countUsersForFilter(users, f.id);
    });
    return map;
  }, [users]);

  const toggleFilter = (id) => {
    setActiveFilters((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const confirmDelete = (user) => {
    setDeleteTarget(user);
    onDeleteOpen();
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget?.id) return;
    try {
      await DeleteUser(deleteTarget.id);
      toast({ title: "User deleted", status: "success" });
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      if (editUserId === deleteTarget.id) setEditUserId(null);
      onDeleteClose();
      setDeleteTarget(null);
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err?.response?.data?.message || err.message,
        status: "error",
      });
    }
  };

  return (
    <Box w="full" maxW="1100px" mx="auto" pb={12} px={{ base: 0, md: 1 }}>
      <Box
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={border}
        bg={shellBg}
        backdropFilter="blur(16px)"
        boxShadow={shellShadow}
        overflow="hidden"
        mb={5}
      >
        <Flex
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          gap={4}
          p={{ base: 4, md: 5 }}
          direction={{ base: "column", md: "row" }}
          borderBottomWidth="1px"
          borderColor={border}
        >
          <Flex align="center" gap={3} minW={0}>
            <Flex
              w={11}
              h={11}
              borderRadius="xl"
              align="center"
              justify="center"
              bgGradient="linear(to-br, brand.300, brand.500)"
              color="gray.900"
              boxShadow="0 4px 16px rgba(52,211,153,0.28)"
              flexShrink={0}
            >
              <Shield size={20} strokeWidth={2.25} />
            </Flex>
            <Box minW={0}>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color={muted}
              >
                Payinapo
              </Text>
              <Heading size="md" letterSpacing="-0.02em" color={titleColor} noOfLines={1}>
                Admin console
              </Heading>
              <Text fontSize="sm" color={muted} mt={0.5}>
                Users, health, and premium grants
              </Text>
            </Box>
          </Flex>
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              borderRadius="full"
              leftIcon={<UserPlus size={15} />}
              bgGradient="linear(to-r, brand.300, brand.500)"
              color="gray.900"
              fontWeight="semibold"
              onClick={() => {
                setEditUserId(null);
                setIsCreating(true);
              }}
            >
              Add user
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderRadius="full"
              leftIcon={<RefreshCw size={15} />}
              onClick={loadUsers}
              isLoading={loading}
            >
              Refresh
            </Button>
            <Button as={RouterLink} to="/shop" size="sm" variant="ghost" borderRadius="full">
              Open app
            </Button>
            <Button
              size="sm"
              variant="ghost"
              borderRadius="full"
              leftIcon={<LogOut size={15} />}
              onClick={lockAdmin}
            >
              Lock
            </Button>
          </HStack>
        </Flex>

        <Tabs variant="unstyled" isLazy>
          <Box px={{ base: 3, md: 5 }} pt={4} pb={2}>
            <TabList
              display="inline-flex"
              gap={1}
              p={1}
              borderRadius="full"
              bg={tabTrackBg}
            >
              <Tab {...tabBase}>Users</Tab>
              <Tab {...tabBase}>Overview</Tab>
              <Tab {...tabBase}>Reports</Tab>
            </TabList>
          </Box>

          <TabPanels>
            <TabPanel px={{ base: 3, md: 5 }} pt={3} pb={5}>
              <Box
                mb={4}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={border}
                bg={filterPanelBg}
                overflow="hidden"
              >
                <Flex
                  px={{ base: 3, md: 4 }}
                  py={3}
                  gap={3}
                  align={{ base: "stretch", sm: "center" }}
                  justify="space-between"
                  direction={{ base: "column", sm: "row" }}
                  borderBottomWidth="1px"
                  borderColor={border}
                  bg={tableBg}
                >
                  <Flex align="center" gap={2} minW={0} flex={1}>
                    <Flex
                      w={8}
                      h={8}
                      borderRadius="lg"
                      align="center"
                      justify="center"
                      bgGradient="linear(to-br, brand.300, brand.500)"
                      color="gray.900"
                      flexShrink={0}
                    >
                      <Filter size={15} strokeWidth={2.25} />
                    </Flex>
                    <Box minW={0} flex={1}>
                      <InputGroup size="sm" maxW={{ base: "full", md: "360px" }}>
                        <InputLeftElement pointerEvents="none">
                          <Search size={15} color="currentColor" opacity={0.5} />
                        </InputLeftElement>
                        <Input
                          placeholder="Search name, email, country…"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          borderRadius="full"
                          bg={inputBg}
                          borderColor={border}
                          _focus={{
                            borderColor: "brand.400",
                            boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)",
                          }}
                        />
                      </InputGroup>
                    </Box>
                  </Flex>
                  <Flex align="center" gap={2} flexShrink={0} justify={{ base: "space-between", sm: "flex-end" }}>
                    <Text fontSize="sm" color={muted} fontWeight="medium">
                      <Text as="span" color={titleColor} fontWeight="semibold">
                        {filtered.length}
                      </Text>
                      <Text as="span" mx={1} opacity={0.5}>
                        /
                      </Text>
                      {users.length} shown
                    </Text>
                    {activeFilters.length > 0 ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        leftIcon={<X size={12} />}
                        color={muted}
                        onClick={() => setActiveFilters([])}
                      >
                        Clear ({activeFilters.length})
                      </Button>
                    ) : null}
                  </Flex>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                  {ADMIN_FILTER_GROUPS.map((group, groupIndex) => {
                    const options = ADMIN_PROFILE_FILTERS.filter((f) => f.group === group.id);
                    return (
                      <Box
                        key={group.id}
                        p={{ base: 3, md: 4 }}
                        borderRightWidth={{ base: 0, md: groupIndex === 0 ? "1px" : 0 }}
                        borderBottomWidth={{ base: groupIndex === 0 ? "1px" : 0, md: 0 }}
                        borderColor={border}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          letterSpacing="0.08em"
                          textTransform="uppercase"
                          color={groupLabelColor}
                          mb={0.5}
                        >
                          {group.title}
                        </Text>
                        <Text fontSize="xs" color={muted} mb={3}>
                          {group.hint}
                        </Text>
                        <Flex gap={1.5} flexWrap="wrap">
                          {options.map((f) => {
                            const on = activeFilters.includes(f.id);
                            const count = filterCounts[f.id] ?? 0;
                            return (
                              <Button
                                key={f.id}
                                size="sm"
                                h="auto"
                                py={1.5}
                                px={2.5}
                                borderRadius="lg"
                                variant="unstyled"
                                display="inline-flex"
                                alignItems="center"
                                gap={2}
                                bg={on ? filterActiveBg : "transparent"}
                                borderWidth="1px"
                                borderColor={on ? "brand.400" : border}
                                boxShadow={on ? "0 0 0 1px var(--chakra-colors-brand-400)" : "none"}
                                color={optionLabelColor}
                                fontWeight={on ? "semibold" : "medium"}
                                onClick={() => toggleFilter(f.id)}
                                _hover={{ bg: on ? filterActiveBg : filterIdleHover }}
                                transition="background 0.15s, border-color 0.15s, box-shadow 0.15s"
                              >
                                <Text as="span" fontSize="xs">
                                  {f.label}
                                </Text>
                                <Badge
                                  borderRadius="full"
                                  px={1.5}
                                  minW="22px"
                                  textAlign="center"
                                  fontSize="10px"
                                  fontWeight="bold"
                                  bg={on ? "brand.500" : countIdleBg}
                                  color={on ? "gray.900" : countIdleColor}
                                >
                                  {count}
                                </Badge>
                              </Button>
                            );
                          })}
                        </Flex>
                      </Box>
                    );
                  })}
                </SimpleGrid>

                {activeFilters.length > 0 ? (
                  <Flex
                    px={{ base: 3, md: 4 }}
                    py={2.5}
                    gap={2}
                    align="center"
                    flexWrap="wrap"
                    borderTopWidth="1px"
                    borderColor={border}
                    bg={tableBg}
                  >
                    <Text fontSize="xs" color={muted} fontWeight="medium">
                      Active:
                    </Text>
                    {activeFilters.map((id) => {
                      const def = ADMIN_PROFILE_FILTERS.find((f) => f.id === id);
                      if (!def) return null;
                      return (
                        <Badge
                          key={id}
                          as="button"
                          type="button"
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                          borderRadius="full"
                          px={2.5}
                          py={1}
                          fontSize="xs"
                          fontWeight="semibold"
                          bgGradient="linear(to-r, brand.300, brand.500)"
                          color="gray.900"
                          cursor="pointer"
                          onClick={() => toggleFilter(id)}
                          _hover={{ opacity: 0.9 }}
                        >
                          {def.group === "gaps" ? `No ${def.label.toLowerCase()}` : def.label}
                          <X size={11} strokeWidth={2.5} />
                        </Badge>
                      );
                    })}
                    <Text fontSize="xs" color={muted}>
                      Matching all selected
                    </Text>
                  </Flex>
                ) : null}
              </Box>

              <Box
                borderRadius="xl"
                borderWidth="1px"
                borderColor={border}
                overflow="hidden"
                bg={tableBg}
              >
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th borderColor={border}>Name</Th>
                        <Th borderColor={border}>Email</Th>
                        <Th borderColor={border}>Country</Th>
                        <Th borderColor={border}>Age</Th>
                        <Th borderColor={border}>Gender</Th>
                        <Th borderColor={border}>Status</Th>
                        <Th borderColor={border} textAlign="right">
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {loading ? (
                        <Tr>
                          <Td colSpan={7} borderColor={border}>
                            <Text color={muted} py={8} textAlign="center" fontSize="sm">
                              Loading users…
                            </Text>
                          </Td>
                        </Tr>
                      ) : filtered.length === 0 ? (
                        <Tr>
                          <Td colSpan={7} borderColor={border}>
                            <Text color={muted} py={8} textAlign="center" fontSize="sm">
                              No users found.
                            </Text>
                          </Td>
                        </Tr>
                      ) : (
                        pageItems.map((user) => (
                          <Tr
                            key={user.id}
                            cursor="pointer"
                            _hover={{ bg: rowHover }}
                            onClick={() => {
                              setIsCreating(false);
                              setEditUserId(user.id);
                            }}
                            transition="background 0.15s"
                          >
                            <Td borderColor={border} fontWeight="semibold" color={titleColor}>
                              {userDisplayName(user)}
                            </Td>
                            <Td borderColor={border} fontSize="xs" color={muted}>
                              {user.email || "—"}
                            </Td>
                            <Td borderColor={border} fontSize="xs">
                              {user.country || "—"}
                            </Td>
                            <Td borderColor={border}>{user.age ?? "—"}</Td>
                            <Td borderColor={border} fontSize="xs" textTransform="capitalize">
                              {formatUserGender(user)}
                            </Td>
                            <Td borderColor={border}>
                              <Flex gap={1} flexWrap="wrap">
                                {user.isProfileComplete ? (
                                  <Badge
                                    borderRadius="full"
                                    px={2}
                                    fontSize="10px"
                                    bg="brand.500"
                                    color="gray.900"
                                  >
                                    Complete
                                  </Badge>
                                ) : (
                                  <Badge borderRadius="full" px={2} fontSize="10px" colorScheme="orange">
                                    Incomplete
                                  </Badge>
                                )}
                                {(user.isVerified || user.verified) && (
                                  <Badge borderRadius="full" px={2} fontSize="10px" colorScheme="blue">
                                    Verified
                                  </Badge>
                                )}
                              </Flex>
                            </Td>
                            <Td borderColor={border} onClick={(e) => e.stopPropagation()}>
                              <Flex justify="flex-end" gap={1}>
                                <IconButton
                                  aria-label="View profile"
                                  size="sm"
                                  variant="ghost"
                                  borderRadius="full"
                                  icon={<ExternalLink size={15} />}
                                  as={RouterLink}
                                  to={`/profile/${user.id}?admin=1`}
                                  state={{ adminView: true }}
                                />
                                <IconButton
                                  aria-label="Delete user"
                                  size="sm"
                                  variant="ghost"
                                  borderRadius="full"
                                  colorScheme="red"
                                  icon={<Trash2 size={15} />}
                                  onClick={() => confirmDelete(user)}
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
                {!loading && filtered.length > 0 ? (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={3}
                    flexWrap="wrap"
                    px={{ base: 3, md: 4 }}
                    py={3}
                    borderTopWidth="1px"
                    borderColor={border}
                  >
                    <Text fontSize="xs" color={muted}>
                      Showing {rangeStart}–{rangeEnd} of {filtered.length} · {USERS_PAGE_SIZE} per page
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        leftIcon={<ChevronLeft size={16} />}
                        isDisabled={!canPrev}
                        onClick={() => setPage(safePage - 1)}
                      >
                        Previous
                      </Button>
                      <Text fontSize="xs" color={muted} minW="64px" textAlign="center">
                        {safePage + 1} / {totalPages}
                      </Text>
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="full"
                        rightIcon={<ChevronRight size={16} />}
                        isDisabled={!canNext}
                        onClick={() => setPage(safePage + 1)}
                      >
                        Next
                      </Button>
                    </HStack>
                  </Flex>
                ) : null}
              </Box>
            </TabPanel>

            <TabPanel px={{ base: 3, md: 5 }} pt={3} pb={5}>
              <AdminDashboard users={users} loading={loading} />
            </TabPanel>

            <TabPanel px={{ base: 3, md: 5 }} pt={3} pb={5}>
              <AdminReports users={users} onUserBanned={loadUsers} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <AdminUserEditor
        mode={isCreating ? "create" : "edit"}
        userId={isCreating ? null : editUserId}
        isOpen={isCreating || !!editUserId}
        onClose={() => {
          setIsCreating(false);
          setEditUserId(null);
        }}
        onSaved={loadUsers}
      />

      <AlertDialog isOpen={deleteOpen} leastDestructiveRef={deleteRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={border}
            bg={shellBg}
          >
            <AlertDialogHeader color={titleColor}>Delete user permanently?</AlertDialogHeader>
            <AlertDialogBody color={muted}>
              This removes{" "}
              <Text as="span" fontWeight="semibold" color={titleColor}>
                {deleteTarget ? userDisplayName(deleteTarget) : "this user"}
              </Text>{" "}
              and cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={deleteRef} variant="ghost" borderRadius="full" onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" borderRadius="full" onClick={handleDeleteUser}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
